import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const alertVariants = cva(
    'relative w-full rounded-lg border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg~*]:pl-7',
    {
        variants: {
            variant: {
                default:
                    'bg-background border-border text-foreground [&>h5]:text-foreground [&>svg]:text-foreground',
                destructive:
                    'bg-destructive/5 border-destructive text-destructive dark:border-destructive dark:text-destructive [&>h5]:text-destructive [&>svg]:text-destructive',
                success:
                    'bg-green-500/15 border-green-500/20 text-green-600 dark:text-green-400 [&>h5]:text-green-600 dark:[&>h5]:text-green-400 [&>svg]:text-green-600 dark:[&>svg]:text-green-400',
                warning:
                    'bg-warning/5 border-warning text-warning dark:border-warning dark:text-warning [&>h5]:text-warning [&>svg]:text-warning',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
)

const Alert = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
    <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
    />
))
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
    <h5
        ref={ref}
        className={cn(
            'mb-1 font-medium leading-none tracking-tight',
            className,
        )}
        {...props}
    />
))
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
    HTMLParagraphElement,
    React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn('text-sm [&_p]:leading-relaxed', className)}
        {...props}
    />
))
AlertDescription.displayName = 'AlertDescription'

export { Alert, AlertTitle, AlertDescription }
